import { ContractorPackageLineItem } from "catchment-common/interfaces/contractorPackage";
import { ProjectLineItem } from "catchment-common/interfaces/project";

/**
 * Which commitment sections a project and package actually have.
 *
 * This is the section registry in predicate form — the same enumeration
 * `SectionId` carries in `catchment-common`, answered per project. One
 * interface with five flags, five predicates beside it, and a fold.
 *
 * ## Why it is here and not split across the domains
 *
 * The plan called for these five predicates to move onto the four section
 * descriptors as an `isVisible` field, so that a domain would own the rule for
 * its own visibility. That was deferred twice on the grounds that a composer
 * needs a layer allowed to see every domain, and then measured properly once
 * `surfaces/` existed. The measurement said not to do it:
 *
 * - **Every one of the sixteen consumers takes the whole set.** They call
 *   `getSectionVisibility` or destructure all five flags — the sidebar, the
 *   package dashboard, the submission wizard, the confirmation step, the
 *   export. Not one wants a single domain's answer, which is the case a
 *   per-descriptor `isVisible` would serve well.
 * - **A composer would have no legal home.** It must import every domain, so it
 *   would sit at `surfaces/` or above; but `useReportExport` under `hooks/` is
 *   one of the callers, and `hooks/` is beside the stack rather than in it.
 * - **Half of it is worse than none.** Predicates on the descriptors AND a fold
 *   here means two sources of truth for the same five flags.
 *
 * Governance settles it. It has no section descriptor at all — `routes.ts`
 * mounts its level pages directly rather than putting it on the DashboardShell
 * — yet `isDocumentsVisible` is one of the five. A field on a descriptor cannot
 * hold a rule for a domain that has no descriptor.
 *
 * So it stays one module, and what changed is where that module lives: out of
 * `utils/`, which is not a layer, into `shared/`, which is. It qualifies on the
 * usual test — its only imports are the project and package interfaces — and
 * from `shared/` every caller can reach it without crossing a rule, including
 * the one under `hooks/` that made the split impossible.
 *
 * If a domain ever needs to answer this for itself, the honest move is to give
 * governance a descriptor first, then move all five together.
 */
export interface SectionVisibility {
  isLabourHoursVisible: boolean;
  isSocialSpendVisible: boolean;
  isLocalContentVisible: boolean;
  isDocumentsVisible: boolean;
  isEnvironmentalVisible: boolean;
}

/**
 * Helper function to check if an array has content
 */
const isExisted = <T>(arr?: T[]): arr is T[] => {
  return Array.isArray(arr) && arr.length > 0;
};

/**
 * Check if labour hours section should be visible
 */
export const isLabourHoursVisible = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): boolean => {
  return (
    isExisted(project?.commitmentsHours) &&
    isExisted(contractorPackage?.commitmentsHours)
  );
};

/**
 * Check if social spend section should be visible
 */
export const isSocialSpendVisible = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): boolean => {
  return (
    isExisted(project?.commitmentsSpend) &&
    isExisted(contractorPackage?.commitmentsSpend)
  );
};

/**
 * Check if local content section should be visible
 */
export const isLocalContentVisible = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): boolean => {
  return (
    isExisted(project?.localContents) &&
    isExisted(contractorPackage?.localContentTargets)
  );
};

/**
 * Check if policies section should be visible
 */
export const isDocumentsVisible = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): boolean => {
  return isExisted(contractorPackage?.policies) && isExisted(project?.policies);
};

/**
 * Get all section visibility states
 */
export const isEnvironmentalVisible = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): boolean => {
  return (
    isExisted(project?.commitmentsEnvironmental) &&
    isExisted(contractorPackage?.commitmentsEnvironmental)
  );
};

export const getSectionVisibility = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): SectionVisibility => {
  return {
    isDocumentsVisible: isDocumentsVisible(project, contractorPackage),
    isEnvironmentalVisible: isEnvironmentalVisible(project, contractorPackage),
    isLabourHoursVisible: isLabourHoursVisible(project, contractorPackage),
    isLocalContentVisible: isLocalContentVisible(project, contractorPackage),
    isSocialSpendVisible: isSocialSpendVisible(project, contractorPackage)
  };
};

/**
 * Check if any commitment sections are visible
 */
export const hasAnyCommitments = (
  project?: ProjectLineItem,
  contractorPackage?: ContractorPackageLineItem
): boolean => {
  const visibility = getSectionVisibility(project, contractorPackage);
  return (
    visibility.isLabourHoursVisible ||
    visibility.isSocialSpendVisible ||
    visibility.isLocalContentVisible
  );
};
